<template>
  <div
    v-if="$store.getters.userId != ''"
    class="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 py-6 px-2"
  >
    <div class="max-w-4xl mx-auto">
      <div class="bg-white rounded-3xl shadow-2xl overflow-hidden">
        <!-- Blue Header like other components with + button -->
        <div class="bg-gradient-to-r from-[#fe5722] to-[#d84315] px-6 py-6">
          <div class="justify-between flex items-center space-x-3">
            <div>
              <h2 class="text-2xl font-bold text-white ml-4">የሚሸጡ ንብረቶች</h2>
              <p class="text-sm text-white mt-1">የሚሸጥ ለመለጠፍ + ምልክቱን ይጫኑ</p>
            </div>
            <div class="flex items-center gap-2">
              <div
                @click="goHome"
                class="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center cursor-pointer"
              >
                <svg
                  class="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <!-- Search Bar -->
        <div class="px-6 pt-4 pb-2 bg-white">
          <div class="relative max-w-xl">
            <span
              class="absolute inset-y-0 left-3 flex items-center pointer-events-none"
            >
              <svg
                class="w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
                />
              </svg>
            </span>
            <input
              v-model="searchTerm"
              @input="onSearchInput($event)"
              @focus="onSearchFocus"
              @blur="onSearchBlur"
              type="text"
              placeholder="የሚሸጥ ይፈልጉ: ርዕስ፣ አድራሻ፣ አይነት..."
              class="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 placeholder-gray-400"
            />
            <div
              v-if="searching"
              class="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <svg
                class="w-5 h-5 text-blue-600 animate-spin"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </div>
          </div>
        </div>

        <div
          v-if="loading"
          class="flex flex-col justify-center items-center h-64 space-y-4"
        >
          <div class="relative">
            <div
              class="w-16 h-16 border-4 border-orange-200 rounded-full animate-spin"
            ></div>
            <div
              class="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-orange-600 rounded-full animate-spin"
            ></div>
          </div>
          <div class="text-center">
            <h3 class="text-lg font-semibold text-gray-700 mb-2">
              Loading Sale...
            </h3>
            <p class="text-gray-500">እባክዎን ትንሽ ይጠብቁ...</p>
          </div>
        </div>
        <div v-else-if="error" class="p-8 text-center text-red-600">
          {{ error.message }}
        </div>
        <div
          v-else-if="!rentals || rentals.length === 0"
          class="p-10 text-center text-gray-600"
        >
          <div
            class="mx-auto w-12 h-12 mb-3 rounded-full bg-blue-50 flex items-center justify-center"
          >
            <svg
              class="w-6 h-6 text-blue-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
              />
            </svg>
          </div>
          <p class="text-lg font-semibold text-gray-800">ምንም ውጤት አልተገኘም</p>
          <p class="text-sm text-gray-500 mt-1">ፍለጋዎን ያስተካክሉ ወይም እንደገና ይሞክሩ</p>
        </div>

        <div v-else class="divide-y">
          <div
            v-for="rental in rentals"
            :key="rental.rent_id"
            class="relative p-4 px-2 hover:bg-gray-50 transition-colors cursor-pointer"
            role="button"
            tabindex="0"
            @click="openRental(rental)"
            @keydown.enter="openRental(rental)"
          >
            <div class="flex items-start gap-4">
              <!-- Media preview: show video if exists else first image -->
              <div
                class="w-32 h-20 flex items-center justify-center bg-gray-100 rounded-md overflow-hidden"
              >
                <template
                  v-if="
                    rental.videos.split(',')[0] != '' &&
                    rental.videos.split(',')[0] != null
                  "
                >
                  <!-- <video
                    :src="rental.videos.split(',')[0]"
                    class="w-full h-full object-cover"
                    muted
                    playsinline
                  ></video> -->
                  <img
                    :src="firstImage(rental)"
                    class="w-full h-full object-cover"
                    alt=" No image"
                  />
                </template>
                <template v-else>
                  <img
                    :src="firstImage(rental)"
                    class="w-full h-full object-cover"
                    alt=" No image"
                  />
                </template>
              </div>

              <div class="flex-1 min-w-0">
                <h3 class="text-base font-bold text-gray-900 truncate">
                  {{ rental.title }}
                </h3>
                <div class="text-sm text-gray-600 mt-0.5">
                  <span>{{ rental.address }}</span>
                </div>
                <p
                  v-if="rental.description"
                  class="text-sm text-gray-700 mt-1 clamp-2"
                >
                  {{ rental.description.replace(/\\n/g, " ") }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apolloClient } from "@/apollo"; // your Apollo client instance
import { gql } from "@apollo/client/core";
import { toast } from "@/toast";

export default {
  data() {
    return {
      user_id: "",
      rentals: [],
      loading: false,
      error: null,
      isExpanded: {},
      searchTerm: "",
      searchTimer: null,
      searchInFlight: false,
      searchInputFocused: false,
      searching: false,
    };
  },

  computed: {},

  watch: {
    searchTerm: {
      handler() {
        if (this.searchTimer) {
          clearTimeout(this.searchTimer);
        }
        this.searching = true;
        this.searchInFlight = true;
        this.searchTimer = setTimeout(() => {
          this.performSearch();
        }, 500);
      },

      immediate: false,
    },
  },

  methods: {
    sortRentalsDesc(list) {
      if (!Array.isArray(list)) return [];
      const toMs = (v) => {
        if (v == null) return 0;
        if (typeof v === "number")
          return String(v).length === 10 ? v * 1000 : v;
        if (typeof v === "string" && /^\d+$/.test(v)) {
          const num = Number(v);
          return String(num).length === 10 ? num * 1000 : num;
        }
        const parsed = Date.parse(v);
        return isNaN(parsed) ? 0 : parsed;
      };
      return [...list].sort(
        (a, b) => toMs(b?.posted_date) - toMs(a?.posted_date)
      );
    },
    openRental(rental) {
      const id = rental && rental.rent_id ? rental.rent_id : null;
      if (!id) return;
      this.$router
        .push({
          name: "singleRental",
          params: { id },
        })
        .catch(() => {
          return;
        });
    },
    onSearchInput(e) {
      // watcher handles debounce; keep spinner live
      this.searching = true;
      this.searchTerm = e.target.value;
    },
    async performSearch() {
      const q = (this.searchTerm || "").trim();
      if (!q) {
        // empty search -> fetch all
        await this.fetchRentals();
        // Always stop spinner after any result
        this.searching = false;
        this.searchInFlight = false;
        return;
      }

      const SEARCH_RENTALS = gql`
        query SearchRentals($q: String!) {
          searchRentals(q: $q) {
            rent_id
            title
            address
            price
            phone
            description
            images
            videos
            isapproved
            posted_date
            message_id
            bank_slip
            user_id
          }
        }
      `;

      try {
        const result = await apolloClient.query({
          query: SEARCH_RENTALS,
          variables: { q },
          fetchPolicy: "no-cache",
        });
        const list =
          result.data && result.data.searchRentals
            ? result.data.searchRentals
            : [];
        if (Array.isArray(list)) {
          this.rentals = this.sortRentalsDesc(list);
          // If no results, end spinner immediately
          if (list.length === 0) {
            this.searching = false;
            this.searchInFlight = false;
            return;
          }
        } else {
          // Fallback to client-side filter if unexpected shape
          this.rentals = this.sortRentalsDesc(
            this.rentals.filter((r) => {
              const hay =
                `${r.title} ${r.address} ${r.description}`.toLowerCase();
              return hay.includes(q.toLowerCase());
            })
          );
          if (this.rentals.length === 0) {
            this.searching = false;
            this.searchInFlight = false;
            return;
          }
        }
      } catch (e) {
        // Backend search not available -> fallback to client-side filter on last full list
        try {
          // Ensure we have a fresh base list to filter
          const original = await apolloClient.query({
            query: gql`
              query GetRentalsAll {
                rentals {
                  rent_id
                  title
                  address
                  price
                  phone
                  description
                  images
                  videos
                  isapproved
                  posted_date
                  message_id
                  bank_slip
                  user_id
                }
              }
            `,
            fetchPolicy: "no-cache",
          });
          const base = original.data?.rentals || [];
          const lower = q.toLowerCase();
          this.rentals = this.sortRentalsDesc(
            base.filter((r) =>
              `${r.title} ${r.address} ${r.description}`
                .toLowerCase()
                .includes(lower)
            )
          );
          if (this.rentals.length === 0) {
            this.searching = false;
            this.searchInFlight = false;
            return;
          }
        } catch (_) {
          void 0;
        }
      } finally {
        this.searchInFlight = false;
        // Always stop spinner after any result
        this.searching = false;
      }

      this.searching = false;
    },
    onSearchFocus() {
      this.searchInputFocused = true;
    },
    onSearchBlur() {
      this.searchInputFocused = false;
      // If no request in flight, stop spinner on blur
      if (!this.searchInFlight) {
        this.searching = false;
      }
    },
    goHome() {
      this.$router
        .push({
          name: "home",
        })
        .catch(() => {
          return;
        });
    },
    firstImage(rental) {
      const imgs = (rental.images || "")
        .split(",")
        .map((s) => s && s.trim())
        .filter(Boolean);
      return imgs[0] || "";
    },
    firstVideo(rental) {
      const vids = (rental.videos || "")
        .split(",")
        .map((s) => s && s.trim())
        .filter(Boolean);
      return vids[0] || "";
    },
    toggleExpand(id) {
      this.$set
        ? this.$set(this.isExpanded, id, !this.isExpanded[id])
        : (this.isExpanded = {
            ...this.isExpanded,
            [id]: !this.isExpanded[id],
          });
    },
    formatDate(input) {
      if (!input) return "-";

      let date;
      // If it's a number or numeric string, treat as UNIX timestamp
      if (
        typeof input === "number" ||
        (typeof input === "string" && /^\d+$/.test(input))
      ) {
        const num = Number(input);
        // If seconds (10 digits), convert to ms
        const ms = String(num).length === 10 ? num * 1000 : num;
        date = new Date(ms);
      } else {
        // Try to parse ISO/MySQL datetime string
        const parsed = Date.parse(input);
        date = isNaN(parsed) ? null : new Date(parsed);
      }

      if (!date || isNaN(date.getTime())) return "-";

      const pad = (n) => String(n).padStart(2, "0");
      const yyyy = String(date.getFullYear());
      const mm = pad(date.getMonth() + 1);
      const dd = pad(date.getDate());
      const hh = pad(date.getHours());
      const min = pad(date.getMinutes());
      const ss = pad(date.getSeconds());
      return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
    },

    async fetchRentals() {
      this.loading = true;
      this.error = null;

      const GET_RENTALS = gql`
        query GetRentalsAll {
          rentalsList {
            rent_id
            title
            address
            price
            phone
            description

            images
            videos
            isapproved
            posted_date
            message_id
            bank_slip
            user_id
          }
        }
      `;

      try {
        const result = await apolloClient.query({
          query: GET_RENTALS, // Your gql query
          fetchPolicy: "no-cache", // Optional, ensures fresh data
        });

        this.rentals = this.sortRentalsDesc(result.data.rentalsList);
      } catch (err) {
        console.log(err);

        toast.error("ምንም ውጤት አልተገኘም። እባክዎን እንደገና ይሞክሩ");
      } finally {
        this.loading = false;
      }
    },
  },

  async mounted() {
    await this.fetchRentals();
  },
};
</script>
